import {
  ArrowBack,
  CloudUpload,
  CreateNewFolder,
  Folder,
  GridView,
  List as ListIcon,
  Person,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/layout/Header";
import TwoPanelLayout from "../components/layout/TwoPanelLayout";
import ChildSelector from "../components/ui/ChildSelector";
import { useAppState } from "../contexts/AppStateContext";
import { useAuth } from "../contexts/AuthContext";
import { fetchChildrenByCampus } from "../services/childDataService";
import { getPhotosByChild } from "../services/photoService";
import type { Child } from "../types";
import type { Photo } from "../types/api";
import { formatDate } from "../utils/formatting";

// Define structure for organizing photos by date folders
interface DateFolder {
  key: string;
  displayName: string;
  photos: Photo[];
  lastModified: Date;
}

interface ChildPhotoStructure {
  dateFolders: DateFolder[];
}

interface OrganizedCampusStructure {
  [childId: string]: ChildPhotoStructure;
}

const CenterDetail: React.FC = () => {
  const { centerId } = useParams<{ centerId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { state, dispatch } = useAppState();

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderDate, setNewFolderDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [campusStructure, setCampusStructure] =
    useState<OrganizedCampusStructure | null>(null);
  const [structureLoading, setStructureLoading] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState<string>("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  useEffect(() => {
    if (centerId && centerId !== state.selectedCampusId) {
      dispatch({ type: "SELECT_CAMPUS", payload: centerId });
    }
  }, [centerId, dispatch, state.selectedCampusId]);

  // Restore child selection from global state when navigating back
  useEffect(() => {
    if (state.selectedChildId && !selectedChildId) {
      setSelectedChildId(state.selectedChildId);
    }
  }, [state.selectedChildId, selectedChildId]);

  // Load children data for the campus
  useEffect(() => {
    const loadChildren = async () => {
      if (!centerId) return;

      try {
        setLoading(true);
        setError("");
        const childrenData = await fetchChildrenByCampus(centerId);
        setChildren(childrenData);

        // Also store in global state
        dispatch({
          type: "SET_CHILDREN",
          payload: { campusId: centerId, children: childrenData },
        });
      } catch (error) {
        console.error("Error loading children:", error);
        setError("Failed to load children. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadChildren();
  }, [centerId, dispatch]);

  // Fetch folder structure from Firestore photos - only for selected child
  useEffect(() => {
    const fetchFolderStructure = async () => {
      // Only fetch if a child is selected
      if (!centerId || !selectedChildId) {
        setCampusStructure(null);
        setStructureLoading(false);
        return;
      }

      setStructureLoading(true);
      try {
        const structure: OrganizedCampusStructure = {};

        // Fetch photos only for the selected child
        try {
          const photos = await getPhotosByChild(centerId, selectedChildId);

          // Group photos by folderId (UUID)
          const photosByFolder: { [key: string]: Photo[] } = {};

          photos.forEach((photo) => {
            // Use folderId if exists (UUID), otherwise fall back to uploadDate (legacy date-based)
            const folderKey =
              photo.folderId ||
              photo.metadata?.folderId ||
              photo.metadata?.uploadDate;

            if (!photosByFolder[folderKey]) {
              photosByFolder[folderKey] = [];
            }
            photosByFolder[folderKey].push(photo);
          });

          // Convert to DateFolder structure
          const dateFolders: DateFolder[] = Object.entries(photosByFolder).map(
            ([folderKey, photos]) => {
              // Get folder name from first photo, with fallback to formatted date
              const displayName =
                photos[0]?.folderName || formatDate(folderKey);
              const lastModified = photos
                .map((p) => new Date(p.createdAt))
                .sort((a, b) => b.getTime() - a.getTime())[0];

              return {
                key: folderKey, // UUID or date string
                displayName,
                photos,
                lastModified,
              };
            },
          );

          // Sort by date descending
          dateFolders.sort(
            (a, b) => b.lastModified.getTime() - a.lastModified.getTime(),
          );

          structure[selectedChildId] = { dateFolders };
        } catch (error) {
          console.error(
            `Error fetching photos for child ${selectedChildId}:`,
            error,
          );
          structure[selectedChildId] = { dateFolders: [] };
        }

        setCampusStructure(structure);
      } catch (error) {
        console.error("Error fetching folder structure:", error);
      } finally {
        setStructureLoading(false);
      }
    };

    fetchFolderStructure();
  }, [centerId, selectedChildId]);

  const handleBack = () => {
    dispatch({ type: "RESET_SELECTIONS" });
    navigate("/centers");
  };

  const handleChildSelect = (childId: string) => {
    setSelectedChildId(childId);
    dispatch({ type: "SELECT_CHILD", payload: childId });
  };

  const handleFolderSelect = (folderId: string) => {
    if (state.selectedChildId) {
      const folder = folders.find((f) => f.id === folderId);
      dispatch({ type: "SELECT_FOLDER", payload: folderId });
      navigate(
        `/center/${centerId}/child/${state.selectedChildId}/folder/${folderId}`,
        {
          state: {
            folderName: folder?.name || formatDate(folderId),
            folderId: folderId,
          },
        },
      );
    }
  };

  const handleCreateFolder = () => {
    if (!state.selectedChildId) return;

    const newFolder = {
      id: crypto.randomUUID(), // Generate unique UUID to prevent same-day folder collisions
      name: newFolderName || newFolderDate,
      date: newFolderDate,
      photoCount: 0,
      createdBy: currentUser?.email?.split("@")[0] || "User",
      dateModified: new Date().toISOString().split("T")[0],
    };

    dispatch({
      type: "ADD_FOLDER",
      payload: { childId: state.selectedChildId, folder: newFolder },
    });

    navigate(
      `/center/${centerId}/child/${state.selectedChildId}/folder/${newFolder.id}`,
      { state: { folderName: newFolder.name, folderId: newFolder.id } },
    );

    setFolderDialogOpen(false);
    setNewFolderName("");
    setNewFolderDate(new Date().toISOString().split("T")[0]);
  };

  // Filter and sort children
  const filteredChildren = children.filter((child) => {
    const fullName = `${child.firstName || ""} ${child.lastName || ""}`.trim();
    const displayName = child.name || fullName;
    const classroom = child.class || child.grade || "";

    return (
      displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classroom.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const sortedChildren = [...filteredChildren].sort((a, b) => {
    const nameA = a.name || `${a.firstName || ""} ${a.lastName || ""}`.trim();
    const nameB = b.name || `${b.firstName || ""} ${b.lastName || ""}`.trim();
    return nameA.localeCompare(nameB);
  });

  const selectedChild = children.find(
    (child) => child.childID === selectedChildId,
  );

  // Get child structure from Firebase Storage using childId
  const selectedChildStructure =
    selectedChild && campusStructure
      ? campusStructure[selectedChild.childID || ""]
      : null;

  // Transform folder structure to flat list for display
  const folders = selectedChildStructure
    ? selectedChildStructure.dateFolders.map((dateFolder) => ({
        id: dateFolder.key,
        name: dateFolder.displayName,
        photoCount: dateFolder.photos.length,
        dateModified: dateFolder.lastModified,
      }))
    : [];

  // Left Panel - Children Selector
  const leftPanel = loading ? (
    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
      <CircularProgress />
    </Box>
  ) : (
    <ChildSelector
      children={sortedChildren}
      selectedChild={selectedChildId}
      searchTerm={searchTerm}
      onSelectChild={handleChildSelect}
      onSearchTermChange={setSearchTerm}
    />
  );

  // Right Panel - Figma-style Grid/List View
  const rightPanel = (
    <Box sx={{ p: 3 }}>
      {structureLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : selectedChild ? (
        <>
          {/* Header with View Toggle and Upload Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                onClick={() => setViewMode("grid")}
                sx={{
                  bgcolor: viewMode === "grid" ? "primary.main" : "transparent",
                  color: viewMode === "grid" ? "white" : "text.primary",
                  "&:hover": {
                    bgcolor:
                      viewMode === "grid" ? "primary.dark" : "action.hover",
                  },
                }}
              >
                <GridView />
              </IconButton>
              <IconButton
                onClick={() => setViewMode("list")}
                sx={{
                  bgcolor: viewMode === "list" ? "primary.main" : "transparent",
                  color: viewMode === "list" ? "white" : "text.primary",
                  "&:hover": {
                    bgcolor:
                      viewMode === "list" ? "primary.dark" : "action.hover",
                  },
                }}
              >
                <ListIcon />
              </IconButton>
            </Box>
            <Button
              variant="contained"
              startIcon={<CreateNewFolder />}
              onClick={() => setFolderDialogOpen(true)}
            >
              New Folder
            </Button>
          </Box>

          {/* Folder Display */}
          {folders.length > 0 ? (
            viewMode === "grid" ? (
              // Grid View - 2 columns
              <Grid container spacing={2}>
                {folders.map((folder) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={folder.id}>
                    <Card
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "primary.main",
                          boxShadow: "0 4px 12px rgba(137, 44, 139, 0.15)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Folder
                          sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, mb: 0.5 }}
                          >
                            {folder.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {folder.photoCount} photo
                            {folder.photoCount !== 1 ? "s" : ""}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {`Last updated: ${formatDate(
                              folder.dateModified.toISOString(),
                            )}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="outlined"
                        fullWidth
                        size="small"
                        sx={{ borderRadius: 2 }}
                        onClick={() => handleFolderSelect(folder.id)}
                      >
                        Open Folder
                      </Button>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              // List/Table View
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1.5fr 1fr 1.5fr",
                    gap: 2,
                    p: 2,
                    bgcolor: "grey.50",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "text.secondary" }}
                  >
                    Name
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "text.secondary" }}
                  >
                    Photos
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "text.secondary" }}
                  >
                    Date modified
                  </Typography>
                </Box>
                {folders.map((folder, index) => (
                  <Box
                    key={folder.id}
                    onClick={() => handleFolderSelect(folder.id)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1.5fr 1fr 1.5fr",
                      gap: 2,
                      p: 2,
                      alignItems: "center",
                      borderBottom:
                        index < folders.length - 1 ? "1px solid" : "none",
                      borderColor: "divider",
                      transition: "background-color 0.2s",
                      "&:hover": { bgcolor: "action.hover", cursor: "pointer" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Folder sx={{ color: "primary.main" }} />
                      <Typography variant="body2">{folder.name}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {folder.photoCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(folder.dateModified.toISOString())}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )
          ) : (
            // Empty State
            <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
              <Folder sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}
              >
                No folders yet
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 400, mx: "auto" }}
              >
                Upload photos to start organizing for{" "}
                {selectedChild.name ||
                  `${selectedChild.firstName || ""} ${
                    selectedChild.lastName || ""
                  }`.trim()}
              </Typography>
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={handleCreateFolder}
              >
                Upload Photos
              </Button>
            </Box>
          )}
        </>
      ) : (
        // No Child Selected State
        <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
          <Person sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}
          >
            Select a child
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a child from the list to view their photo folders
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Header />

      {/* Breadcrumb */}
      <Box sx={{ p: 3, pb: 0 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>
      </Box>

      {/* Two Panel Layout */}
      <TwoPanelLayout
        leftPanel={leftPanel}
        rightPanel={rightPanel}
        leftPanelWidth={5}
      />

      {/* Create Folder Dialog */}
      <Dialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Date"
            type="date"
            fullWidth
            variant="outlined"
            value={newFolderDate}
            onChange={(e) => setNewFolderDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderName}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CenterDetail;
